<template>

  <div class="select-site-filter">

    <el-select
      ref="select"
      :value="value"
      multiple
      filterable
      :remote="isRemote"
      :remote-method="isRemote ? onQuery : undefined"
      :filter-method="isRemote ? undefined : onQuery"
      reserve-keyword
      default-first-option
      clearable
      :disabled="isDisabled"
      :placeholder="placeholder"
      :loading="loading"
      :loading-text="loadingText"
      :no-data-text="noDataText"
      popper-class="brc-site-popper"
      style="width: 100%"
      @input="onInput"
      @visible-change="onVisible">

      <el-option
        v-for="o in visibleOptions"
        :key="o.value"
        :label="o.label"
        :value="o.value"
        :disabled="!!o.cap"
        class="select-site-filter__option">
        <template v-if="o.cap">
          <span class="brc-opt-cap">Showing the first {{ siteIdLimit }} — keep typing to narrow</span>
        </template>
        <template v-else-if="isRemote">
          <span class="brc-opt-l">{{ o.siteName || o.label }}</span>
          <span class="brc-opt-r"><span v-if="o.telcoSiteId" class="text-muted">{{ o.telcoSiteId }} | </span>{{ o.value }}</span>
          <span v-if="telcoHint(o)" class="brc-opt-hint"><span class="brc-opt-hint-label">Telco Site ID:</span> {{ o.telcoSiteId }}</span>
        </template>
        <template v-else>
          <span class="brc-opt-l">{{ o.label }}</span>
          <span v-if="countText(o)" class="brc-opt-r">{{ countText(o) }}</span>
        </template>
      </el-option>

    </el-select>

    <p v-if="!organisationId" class="text-muted select-site-filter__hint">Pick a telco first.</p>

  </div>

</template>

<script>

/*
XOL-653 — one remote multi-select for the Bulk licensee rate change filters.
Used five times (Site ID, City, State, District, Site group); each instance has
its own options, query, loading and error state, so five in-flight guards can
never be cross-wired.

  - Remote field (Site ID): searches as the user types, from 3 characters.
    Element's own 300 ms remote debounce is the only debounce.
  - List fields: load the telco's distinct values when the picker OPENS (Element
    2.15 does not call remote-method on the first open, because previousQuery is
    null) and filter on the client.

The atom never clears `value`: the parent owns the picks, and clears them when
the telco changes. The API contract lives in src/lib/bulkRateFilters.js.
*/

import PostMixins from '@/mixins/PostMixins'
import { SITE_FIELDS, CAP_VALUE, NO_GROUP, suggestUrl, normaliseSuggestions } from '@/lib/bulkRateFilters'

function cut (t) {
  return t.length > 40 ? t.slice(0, 40) + '…' : t
}

export default {
  name: 'select-site-filter',

  mixins: [PostMixins],

  props: {
    value: {
      type: Array,
      default: function () { return [] }
    },

    // A SITE_FIELDS key.
    field: {
      type: String,
      required: true,
      validator: function (v) { return Object.prototype.hasOwnProperty.call(SITE_FIELDS, v) }
    },

    // The telco. The picker is disabled until there is one.
    organisationId: {
      type: [Number, String],
      default: null
    },

    // The accessible name (Element 2.15 gives the focused input none).
    label: {
      type: String,
      default: ''
    },

    placeholder: {
      type: String,
      default: ''
    },

    loadingText: {
      type: String,
      default: 'Loading…'
    },

    // Plural noun, for the empty copy and the fetch-error toast ("site groups").
    noun: {
      type: String,
      default: 'values'
    },

    disabled: {
      type: Boolean,
      default: false
    }
  },

  data: function () {
    return {
      allOptions: [],
      query: '',
      loading: false,
      error: false,
      requestSeq: 0
    }
  },

  computed: {
    spec: function () {
      return SITE_FIELDS[this.field]
    },

    isRemote: function () {
      return !!this.spec.remote
    },

    isDisabled: function () {
      return this.disabled || !this.organisationId
    },

    siteIdLimit: function () {
      return this.spec.limit || 0
    },

    // Remote fields show what the server returned; list fields are filtered
    // on the client by a case-insensitive substring match on the label.
    visibleOptions: function () {
      if (this.isRemote || !this.query) return this.allOptions
      var q = this.query.toLowerCase()
      return this.allOptions.filter(function (o) {
        if (String(o.label).toLowerCase().indexOf(q) !== -1) return true
        // "none" also finds the no-group row (the mock does the same).
        return o.value === NO_GROUP && 'none'.indexOf(q) !== -1
      })
    },

    noDataText: function () {
      var q = this.query
      if (this.error) return 'Search failed — see the error message, then try again'
      if (this.isRemote) {
        if (!q) return 'Type at least ' + this.spec.minChars + ' characters to search'
        if (q.length < this.spec.minChars) return 'Keep typing — at least ' + this.spec.minChars + ' characters'
        return 'No ' + this.noun + ' match "' + cut(q) + '"'
      }
      if (q) return 'No ' + this.noun + ' match "' + cut(q) + '"'
      return 'No ' + this.noun + ' for this telco'
    }
  },

  methods: {
    onInput: function (v) {
      // The cap row is disabled, but never let it into the picks.
      this.$emit('input', (v || []).filter(function (x) { return x !== CAP_VALUE }))
    },

    onVisible: function (open) {
      this.query = ''
      if (open) {
        if (!this.isRemote && this.organisationId) this.load('')
        return
      }
      // Closed: a remote picker forgets its last search, so it doesn't reopen
      // showing results for a query the box no longer holds. Selected values
      // keep their chips (Element caches the selected options).
      if (this.isRemote) this.clearOptions()
    },

    // remote-method (Site ID) / filter-method (list fields) — Element passes the raw text.
    onQuery: function (q) {
      var trimmed = (q || '').trim()
      this.query = trimmed
      if (!this.isRemote) return
      if (trimmed.length < this.spec.minChars) {
        this.error = false
        this.clearOptions()
        return
      }
      this.load(trimmed)
    },

    load: function (q) {
      if (!this.organisationId) return
      var self = this
      var seq = ++this.requestSeq
      this.loading = true
      this.error = false
      this.fetchData(suggestUrl(this.organisationId, this.field, q), function (data) {
        if (seq !== self.requestSeq) return
        self.allOptions = normaliseSuggestions(self.field, data)
        self.loading = false
      }, this.noun, function () {
        if (seq !== self.requestSeq) return
        self.allOptions = []
        self.loading = false
        self.error = true
      })
    },

    // Invalidate any in-flight response and empty the list.
    clearOptions: function () {
      this.requestSeq++
      this.allOptions = []
      this.loading = false
    },

    reset: function () {
      this.clearOptions()
      this.query = ''
      this.error = false
    },

    countText: function (o) {
      var n = o.count
      if (n === null || n === undefined || n === '') return ''
      return n + (Number(n) === 1 ? ' site' : ' sites')
    },

    // The match was on the telco's own site ID: say so, as SelectSite does.
    telcoHint: function (o) {
      if (!this.query || !o.telcoSiteId) return false
      var q = this.query.toLowerCase()
      var inValue = String(o.value).toLowerCase().indexOf(q) !== -1
      var inName = !!o.siteName && String(o.siteName).toLowerCase().indexOf(q) !== -1
      return !inValue && !inName && String(o.telcoSiteId).toLowerCase().indexOf(q) !== -1
    },

    // Element 2.15 `multiple filterable` focuses input.el-select__input, which
    // has no id or aria-label binding, and the reference el-input is
    // tabindex=-1 in this mode. So the name goes on the input itself — not on
    // <el-select>, which would put it on the wrapper div.
    applyAriaLabel: function () {
      var select = this.$refs.select
      var input = select && select.$refs && select.$refs.input
      if (!input || !input.setAttribute) return
      if (this.label) {
        input.setAttribute('aria-label', this.label)
      } else {
        input.removeAttribute('aria-label')
      }
    }
  },

  watch: {
    // A different telco: everything on screen was produced for the previous one.
    organisationId: function () {
      this.reset()
    },

    label: function () {
      this.applyAriaLabel()
    }
  },

  mounted: function () {
    this.applyAriaLabel()
  },

  beforeDestroy: function () {
    // A late response must not write into a destroyed component.
    this.requestSeq++
  }
}
</script>

<style scoped>
.select-site-filter__hint {
  font-size: 12px;
  margin: 0;
}
</style>

<style>
/* Non-scoped: the popper is portaled to document.body and does not carry this
   component's data-v attribute. Targeted via popper-class, so no other
   el-select dropdown is affected. Same rules as SelectSite's .ss-main-popper. */
.brc-site-popper .el-select-dropdown__item {
  height: auto;
  min-height: 34px;
  line-height: 1.4;
  padding-top: 6px;
  padding-bottom: 6px;
}
.brc-site-popper .el-select-dropdown__item > span.brc-opt-l,
.brc-site-popper .el-select-dropdown__item > span.brc-opt-r {
  line-height: 22px;
}
.brc-opt-l {
  float: left;
}
.brc-opt-r {
  float: right;
  color: #8492a6;
  font-size: 13px;
  margin-left: 16px;
}
.brc-site-popper.el-select-dropdown.is-multiple .el-select-dropdown__item.selected::after {
  right: 8px;
}
.brc-site-popper.el-select-dropdown.is-multiple .el-select-dropdown__item {
  padding-right: 32px;
}
.brc-opt-hint {
  display: block;
  clear: both;
  font-size: 12px;
  color: #8492a6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.brc-opt-hint-label {
  color: #606266;
  font-weight: 600;
}
.brc-opt-cap {
  font-size: 12px;
  color: #909399;
  font-style: italic;
}
</style>
